import { useState } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { useApiGet, useApiMutation } from "../../utils/api";

const TABS = [
  { key: "all", label: "All" },
  { key: "feeding", label: "Feedings" },
  { key: "diaper_change", label: "Diaper Changes" },
  { key: "pumping", label: "Pumpings" },
];

const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDateOnly = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const typeLabel = (type: string): string => {
  if (type === "feeding") return "Feeding";
  if (type === "pumping") return "Pumping";
  if (type === "weighing") return "Weighing";
  return "Diaper Change";
};

const typeIcon = (type: string): string => {
  if (type === "feeding") return "🍼";
  if (type === "pumping") return "🥛";
  if (type === "weighing") return "⚖️";
  return "💩";
};

const formatWeight = (oz: number): string => {
  const lbs = Math.floor(oz / 16);
  const remainder = oz % 16;
  return remainder === 0 ? `${lbs} lbs` : `${lbs} lbs ${remainder} oz`;
};

interface LogEntry {
  id: number;
  type: string;
  ended_at?: string;
  weighed_on?: string;
  amount_ml?: number | null;
  weight_oz?: number | null;
}

interface DailyAggregateEntry {
  date: string;
  feeding_oz: number;
  pumping_oz: number;
  diaper_change_count: number;
}

const LogsTable = ({
  logs,
  onDelete,
}: {
  logs: LogEntry[];
  onDelete: (log: LogEntry) => void;
}) => (
  <div className="card border-0 shadow-sm">
    <div className="table-responsive">
      <table className="table mb-0">
        <thead>
          <tr>
            <th>Type</th>
            <th>Amount</th>
            <th className="text-end">Time</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={`${log.type}-${log.id}`}>
              <td>
                <span className="mr-2">{typeIcon(log.type)}</span>{" "}
                {typeLabel(log.type)}
              </td>
              <td>
                {(log.type === "feeding" || log.type === "pumping") &&
                log.amount_ml != null ? (
                  <span className="text-muted">{log.amount_ml} ml</span>
                ) : log.type === "weighing" && log.weight_oz != null ? (
                  <span className="text-muted">
                    {formatWeight(log.weight_oz)}
                  </span>
                ) : null}
              </td>
              <td className="text-end">
                <span className="text-muted">
                  {log.type === "weighing"
                    ? log.weighed_on
                    : formatDateTime(log.ended_at!)}
                </span>
              </td>
              <td>
                <button
                  className="btn btn-sm btn-text red"
                  onClick={() => onDelete(log)}
                >
                  <i className="bi bi-x" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const DailyAggregateTable = ({
  dailyAggregates,
}: {
  dailyAggregates: DailyAggregateEntry[];
}) => (
  <div className="card border-0 shadow-sm">
    <div className="table-responsive">
      <table className="table mb-0">
        <thead>
          <tr>
            <th>Date</th>
            <th className="text-end">Feedings (oz)</th>
            <th className="text-end">Pumpings (oz)</th>
            <th className="text-end">Diaper Changes</th>
          </tr>
        </thead>
        <tbody>
          {dailyAggregates.map((entry) => (
            <tr key={entry.date}>
              <td>{formatDateOnly(entry.date)}</td>
              <td className="text-end text-muted">{entry.feeding_oz}</td>
              <td className="text-end text-muted">{entry.pumping_oz}</td>
              <td className="text-end text-muted">
                {entry.diaper_change_count}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const Logs = () => {
  const [activeTab, setActiveTab] = useState("all");

  const { data, unavailable, refetch } = useApiGet("/api/user/logs", {
    type: activeTab,
  });
  const { post } = useApiMutation();

  if (unavailable) return unavailable;

  const logs: LogEntry[] = data?.logs || [];
  const dailyAggregates: DailyAggregateEntry[] = data?.daily_aggregates || [];

  const handleDelete = (log: LogEntry) => {
    if (
      window.confirm(`Delete this ${typeLabel(log.type).toLowerCase()} entry?`)
    ) {
      post("/api/user/delete_log", { id: log.id, type: log.type }).then(() =>
        refetch(),
      );
    }
  };

  return (
    <div className="container py-4">
      <h2 className="mb-4">Activity Logs</h2>

      <Tabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key || "all")}
        className="mb-3"
      >
        {TABS.map((tab) => (
          <Tab key={tab.key} eventKey={tab.key} title={tab.label} />
        ))}
      </Tabs>

      {activeTab === "all" ? (
        <DailyAggregateTable dailyAggregates={dailyAggregates} />
      ) : logs.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5 text-muted">
            {`No ${TABS.find((t) => t.key === activeTab)?.label.toLowerCase()} logged yet`}
          </div>
        </div>
      ) : (
        <LogsTable logs={logs} onDelete={handleDelete} />
      )}
    </div>
  );
};
